import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { APP_VERSION } from '@/config/version';
import posthogLib from 'posthog-js';

/**
 * Shared helper — gets PostHog at call time. No hooks, no state, no closures.
 */
export const getPostHog = () => posthogLib.__loaded ? posthogLib : null;

/**
 * Full PostHog hook with user identification and auth tracking.
 * Call this ONCE at the app root (App.tsx).
 */
export const usePostHogTracking = () => {
  const hasLoggedIn = useRef(false);

  useEffect(() => {
    const ph = getPostHog();
    if (!ph) return;

    ph.register({
      app_version: APP_VERSION,
      environment: import.meta.env.MODE === 'production' ? 'production' : 'development'
    });

    const identifyUser = (user: any) => {
      if (!user) return;
      const ph = getPostHog();
      if (!ph) return;
      ph.identify(user.id, {
        email: user.email,
        user_id: user.id,
        created_at: user.created_at,
        is_authenticated: true,
        provider: user.app_metadata?.provider || 'email',
        email_confirmed: !!user.email_confirmed_at
      });
    };

    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) identifyUser(user);
      } catch (error) {
        console.error('Error checking user for PostHog:', error);
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user);
        if (!hasLoggedIn.current) {
          hasLoggedIn.current = true;
          const ph = getPostHog();
          ph?.capture('user_logged_in', {
            user_id: session.user.id,
            email: session.user.email,
            provider: session.user.app_metadata?.provider || 'email',
            login_method: session.user.app_metadata?.provider || 'email'
          });
        }
      } else if (event === 'SIGNED_OUT') {
        const ph = getPostHog();
        ph?.capture('user_logged_out');
        ph?.reset();
        hasLoggedIn.current = false;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
};

// ── Cart tracking ──

export const trackCartEvent = (eventName: string, data: any) => {
  const ph = getPostHog();
  if (ph) ph.capture(eventName, { ...data, timestamp: new Date().toISOString() });
};

export const trackAddToCart = (_posthog: any, item: any) => {
  trackCartEvent('add_to_cart', {
    item_type: item.type,
    participant_id: item.participantId,
    participant_name: item.participantName,
    horse_name: item.horseName,
    race_name: item.raceName,
    event_name: item.eventName,
    price: item.price,
    currency: 'CHF',
    image_id: item.imageId || null,
    is_collection: item.type === 'collection',
    is_single_photo: item.type === 'single'
  });
};

export const trackSearch = (_posthog: any, query: string, searchType: 'query' | 'participant' | 'rider' | 'horse' = 'query', metadata?: any) => {
  const ph = getPostHog();
  if (ph) ph.capture('search', { search_query: query, search_type: searchType, query_length: query.length, ...metadata });
};

export const trackCartOpen = (_posthog: any, cartData: any) => {
  const ph = getPostHog();
  if (ph) ph.capture('cart_opened', { cart_item_count: cartData.items?.length || 0, cart_total: cartData.total || 0, currency: 'CHF' });
};

export const trackCheckoutInitiated = (_posthog: any, cartData: any) => {
  const ph = getPostHog();
  if (ph) ph.capture('checkout_initiated', {
    cart_item_count: cartData.items?.length || 0,
    cart_total: cartData.total || 0,
    currency: 'CHF',
    items: cartData.items?.map((item: any) => ({ type: item.type, participant_name: item.participantName, price: item.price, quantity: item.quantity })) || []
  });
};

export const trackPaymentSuccess = (_posthog: any, orderData: any) => {
  const ph = getPostHog();
  if (ph) ph.capture('payment_success', {
    order_id: orderData.orderId || orderData.id,
    total_amount: orderData.totalAmount || orderData.total,
    currency: 'CHF',
    payment_method: 'stripe',
    item_count: orderData.itemCount || orderData.items?.length
  });
};

export const trackPageView = (_posthog: any, pageName: string, metadata?: any) => {
  const ph = getPostHog();
  if (ph) ph.capture('page_view', { page_name: pageName, url: window.location.pathname, ...metadata });
};

// ── Upsell tracking ──

const mapUpsellOpportunity = (opp: any) => ({
  participant_id: opp.participantId,
  participant_name: opp.participantName,
  race_id: opp.raceId,
  race_name: opp.raceName,
  event_id: opp.eventId,
  individual_photos_count: opp.currentPhotos,
  individual_photos_cost: opp.currentCost,
  collection_cost: opp.collectionCost,
  savings: opp.delta < 0 ? Math.abs(opp.delta) : 0,
  additional_cost: opp.delta > 0 ? opp.delta : 0
});

export const trackUpsellPresented = (_posthog: any, opportunities: any[]) => {
  const ph = getPostHog();
  if (!ph) return;
  const collections = opportunities.map(mapUpsellOpportunity);
  ph.capture('upsell_presented', {
    collections_count: collections.length,
    collections,
    total_savings: collections.reduce((s, c) => s + c.savings, 0),
    total_additional_cost: collections.reduce((s, c) => s + c.additional_cost, 0),
    currency: 'CHF',
    timestamp: new Date().toISOString()
  });
};

export const trackUpsellAccepted = (_posthog: any, opportunities: any[]) => {
  const ph = getPostHog();
  if (!ph) return;
  const collections = opportunities.map(mapUpsellOpportunity);
  ph.capture('upsell_accepted', {
    collections_count: collections.length,
    collections,
    total_savings: collections.reduce((s, c) => s + c.savings, 0),
    total_additional_cost: collections.reduce((s, c) => s + c.additional_cost, 0),
    currency: 'CHF',
    timestamp: new Date().toISOString()
  });
};

export const trackUpsellRejected = (_posthog: any, opportunities: any[]) => {
  const ph = getPostHog();
  if (!ph) return;
  const collections = opportunities.map(mapUpsellOpportunity);
  ph.capture('upsell_rejected', {
    collections_count: collections.length,
    collections,
    total_savings: collections.reduce((s, c) => s + c.savings, 0),
    total_additional_cost: collections.reduce((s, c) => s + c.additional_cost, 0),
    currency: 'CHF',
    timestamp: new Date().toISOString()
  });
};

// ── Shared cart tracking ──

export const trackSharedCartCreated = (data: {
  share_method: 'email' | 'link';
  kid_name: string;
  item_count: number;
  total_amount: number;
  share_token: string;
}) => {
  const ph = getPostHog();
  if (ph) ph.capture('shared_cart_created', { ...data, currency: 'CHF', timestamp: new Date().toISOString() });
};

export const trackSharedCartViewed = (data: {
  share_token: string;
  kid_name: string;
  item_count: number;
  total_amount: number;
  referrer: string;
  time_since_created: number;
}) => {
  const ph = getPostHog();
  if (ph) ph.capture('shared_cart_viewed', { ...data, currency: 'CHF', timestamp: new Date().toISOString() });
};

export const trackSharedCartItemRemoved = (data: {
  share_token: string;
  item_type: string;
  participant_name: string;
  remaining_items: number;
}) => {
  const ph = getPostHog();
  if (ph) ph.capture('shared_cart_item_removed', { ...data, timestamp: new Date().toISOString() });
};

export const trackSharedCartCheckout = (data: {
  share_token: string;
  kid_name: string;
  item_count: number;
  total_amount: number;
  items_modified: boolean;
}) => {
  const ph = getPostHog();
  if (ph) ph.capture('shared_cart_checkout_initiated', { ...data, currency: 'CHF', timestamp: new Date().toISOString() });
};
